import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {fetchPersonnelCandidates} from '@/entities/personnel/api';
import type {PersonnelCandidate} from '@/entities/personnel/types';
import {formatTimestamp} from '@/shared/lib/format';
import {Badge} from '@/shared/ui/badge';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {ListRow, RowGroup} from '@/shared/ui/list-row';
import {SectionHeader} from '@/shared/ui/section';
import {AppText} from '@/shared/ui/text';
import {spacing, useTheme} from '@/shared/ui/theme';

/**
 * "Do we already know this person?" — asked while a new one is being typed in.
 *
 * Since 2026-08-24 removing somebody is a DETACH: the row leaves the catalogue and the
 * face is erased, but the identity stays findable so a returning employee can be given
 * their own record back instead of a second one. Nothing surfaces that unless a screen
 * asks, and `GET /control/personnel/candidates` is the asking.
 *
 * ## Two kinds of answer, and they are not the same question
 *
 * **Detached** — the same person coming back. Choosing them means the save goes under
 * their `record_id`, which re-attaches the row and keeps their attendance history.
 * That is the whole point of the feature.
 *
 * **Active** — somebody who is working right now under these traits. This is NOT a
 * return; it is an attempt to enrol a second row for one human, and it is offered as a
 * warning with a way to open that card, never as something to save over. Writing under
 * a working employee's id would quietly overwrite them.
 *
 * ## Nothing is preselected, ever
 *
 * A `weak` candidate matched on employee number alone, and numbers of former employees
 * get reused — under number "14" there may well be somebody else today. The contract
 * says a weak candidate must not be preselected; this component preselects NOTHING,
 * which satisfies that for strong matches too. The costs are not symmetric: a spare
 * row is an afternoon's tidying, while confirming the wrong candidate hands a stranger
 * someone else's face and someone else's history.
 */
const styles = StyleSheet.create({
  section: {gap: spacing(2)},
  chosen: {gap: spacing(2)},
});

export function CandidateNotice({
  companyId,
  fullName,
  birthDate,
  employeeNumber,
  chosen,
  onChoose,
  onOpen,
}: {
  companyId: string;
  fullName: string;
  birthDate: string;
  employeeNumber: string;
  /** The detached record the save will reuse, or null for "a different person". */
  chosen: PersonnelCandidate | null;
  onChoose: (candidate: PersonnelCandidate | null) => void;
  /** Open an ACTIVE match's card. Absent ⇒ the warning stands without the shortcut. */
  onOpen?: (recordId: string) => void;
}) {
  const {t} = useTranslation();
  const p = useTheme();

  const name = fullName.trim();
  const number = employeeNumber.trim();
  const birth = birthDate.trim();

  const query = useQuery({
    // Keyed on the traits themselves, so typing re-asks and the answer can never
    // belong to a name that is no longer in the field.
    queryKey: ['personnel-candidates', companyId, name, birth, number],
    queryFn: () =>
      fetchPersonnelCandidates(companyId, {
        fullName: name,
        birthDate: birth,
        employeeNumber: number,
      }),
    // The server answers an empty list without both of these, so there is nothing to
    // ask for yet. A name of one or two letters is still being typed.
    enabled: name.length >= 3 || number.length > 0,
    staleTime: 30_000,
  });

  const candidates = query.data ?? [];
  const detached = candidates.filter(c => c.detached_at);
  const active = candidates.filter(c => !c.detached_at);

  if (candidates.length === 0) {
    return null;
  }

  return (
    <View style={styles.section}>
      <SectionHeader
        title={t('candidates.title')}
        hint={t('candidates.hint')}
      />

      {active.length > 0 ? (
        <Card padding={4} style={styles.chosen}>
          <Banner tone="warning" title={t('candidates.activeTitle')} />
          <AppText variant="caption" color={p.mutedForeground}>
            {t('candidates.activeHint')}
          </AppText>
          <RowGroup>
            {active.map(c => (
              <ListRow
                key={c.record_id}
                icon="personnel"
                title={c.full_name}
                subtitle={t('candidates.meta', {
                  number: c.employee_number || '—',
                  position: c.position || '—',
                })}
                trailing={
                  <Badge
                    variant="warn"
                    size="sm"
                    label={t('candidates.working')}
                  />
                }
                onPress={onOpen ? () => onOpen(c.record_id) : undefined}
                testID={`candidate-active-${c.record_id}`}
              />
            ))}
          </RowGroup>
        </Card>
      ) : null}

      {detached.length > 0 ? (
        <Card padding={4} style={styles.chosen}>
          <AppText variant="caption" color={p.mutedForeground}>
            {t('candidates.detachedHint')}
          </AppText>
          <RowGroup>
            {detached.map(c => {
              const picked = chosen?.record_id === c.record_id;
              return (
                <ListRow
                  key={c.record_id}
                  icon={picked ? 'check' : 'personnel'}
                  title={c.full_name}
                  subtitle={t('candidates.detachedMeta', {
                    number: c.employee_number || '—',
                    when: formatTimestamp(c.detached_at),
                  })}
                  trailing={
                    <Badge
                      variant={c.strength === 'strong' ? 'info' : 'muted'}
                      size="sm"
                      label={t(
                        c.strength === 'strong'
                          ? 'candidates.strong'
                          : 'candidates.weak',
                      )}
                    />
                  }
                  /* Tapping toggles. Nothing arrives selected — see the note above. */
                  onPress={() => onChoose(picked ? null : c)}
                  testID={`candidate-${c.record_id}`}
                />
              );
            })}
          </RowGroup>
          {chosen ? (
            <>
              <Banner
                tone="info"
                title={t('candidates.reusing', {name: chosen.full_name})}
              />
              <AppButton
                title={t('candidates.differentPerson')}
                tone="ghost"
                onPress={() => onChoose(null)}
                testID="candidate-clear"
              />
            </>
          ) : (
            <AppText variant="caption" color={p.mutedForeground}>
              {t('candidates.noneChosen')}
            </AppText>
          )}
        </Card>
      ) : null}
    </View>
  );
}
